import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import browzwearService from "api/browzwear/browzwear.service";
import { IBrowzwearBoard, IBrowzwearFolder } from "api/browzwear/browzwear.type";
import { BrowzwearCredentials } from "components/BrowzwearAPIForm";
import { useSnackbar } from "hooks/useSnackbar";
import { useAppDispatch, useAppSelector } from "store";
import { setBrowzwear } from "store/commonSlice";

/**
 * The one place Browzwear connect + import happens (REF-6494).
 *
 * Three surfaces used to hand-roll this sequence — the standalone `/integrations` page, the Design
 * Canvas assets panel's Integrations section, and the `ExportToBrowzwear` modal — and every copy got
 * a different subset of it wrong:
 *
 *  • `IntegrationsPage` fetched the boards on a 200 and then **never dispatched `setBrowzwear`**, so
 *    `isConnected` stayed false and its own credential form re-rendered on top of the 20 boards it
 *    had just imported. That is the reported "listelenemiyor" — the import was fine all along
 *    (verified on prod: connect 200, import 200 with 20 folders / 109 images).
 *  • None of them checked `_handled`. Per `[HTTP_HANDLED_RESPONSE]` a 401/403/409/503 on this route
 *    RESOLVES with `_handled: true`, so `catch` never runs: a rejected connect produced
 *    `isConnected: true` with an empty `owner_id`, and the next export then went out unauthenticated.
 *
 * `connect()` performs the whole handshake — connect, then import — and commits to Redux only when
 * BOTH calls really succeeded. `refresh()` re-imports with the stored credentials.
 *
 * Credentials are session-scoped by design (product decision, REF-6494): `commonSlice` holds them in
 * memory, nothing persists them, and there is no BE credential endpoint. A reload asks again — which
 * is deliberate, since the alternative is writing an API secret to localStorage.
 */
export const useBrowzwearBoards = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showError } = useSnackbar();
  const { browzwear } = useAppSelector((state) => state.common);

  const [boards, setBoards] = useState<IBrowzwearBoard[]>([]);
  // The FOLDER level, kept beside the flattened boards rather than instead of them (REF-6498).
  // Every consumer used to call `folders.flatMap(f => f.boards)` here and the group died on that
  // line — which is why a Collection (a group whose `group_type` is `collection`) was unreachable in
  // the UI even though `/import` returns it. `boards` stays for the consumers that genuinely want a
  // flat list: the export picker and the Design Canvas shelf preview.
  const [folders, setFolders] = useState<IBrowzwearFolder[]>([]);
  // When the mirror behind the current tree was last refreshed. Shown next to Refresh: serving a
  // cached tree without saying how old it is would be a quiet lie (REF-6498).
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  // Which credential set the boards in state belong to. `boards` is per-hook-instance, so a consumer
  // mounted while the user reconnects to a DIFFERENT store (Account, or another surface's form) would
  // otherwise keep showing the previous store's boards indefinitely — they are not wrong-looking, just
  // wrong. Reconnecting to the same store is not a reset.
  const isConnected = browzwear.isConnected;
  const connectionKey = `${browzwear.storeName}::${browzwear.apiKey}`;
  const loadedConnectionRef = useRef(connectionKey);

  // Drops a response that a newer connect/refresh — or a credential change — has superseded.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (loadedConnectionRef.current === connectionKey) return;
    loadedConnectionRef.current = connectionKey;
    // Bumping the id is what actually invalidates work already in flight. Without it an import
    // started against store A could resolve AFTER the switch to store B and repopulate B's grid
    // with A's boards — clearing state alone does not stop the pending response.
    requestIdRef.current += 1;
    setBoards([]);
    setFolders([]);
    // The freshness label describes the tree; a reset that keeps it would leave "Updated <time>"
    // over an empty grid, which is precisely how the bug above looked from the outside.
    setSyncedAt(null);
    setHasFailed(false);
    setIsLoading(false);
  }, [connectionKey]);

  // A response is only allowed to touch state if nothing has superseded it. Applied to the failure
  // and loading-cleanup paths too, not just success: an older rejection could otherwise clear a
  // newer request's spinner or raise a snackbar for work the user has already moved on from.
  const isCurrent = (requestId: number) => requestId === requestIdRef.current;

  /** Import with an explicit credential set. Returns the FOLDERS, or null when the call did not
   * succeed. Callers flatten to boards themselves — the tree is what the browser needs. */
  const importTree = useCallback(
    async (force = false): Promise<IBrowzwearFolder[] | null> => {
      // No credentials in the body: the server holds the session for this user (REF-6498). They
      // are still ACCEPTED by the API, so this is safe across a split deploy in either direction —
      // an older API reads the session it was just given by `/connect` in the same call sequence.
      //
      // `force` re-walks Stylezone (`/sync`); the default reads the mirror. Two endpoints rather
      // than a flag on one, so the slow path cannot be taken by accident.
      const res = force ? await browzwearService.sync() : await browzwearService.import();

      // A resolved response is not a success — the interceptor already showed the UX for these, so
      // this path stays silent on purpose.
      if (res?._handled) return null;

      setSyncedAt(res?.responseData?.synced_at ?? null);

      const folders = res?.responseData?.folders;
      if (!Array.isArray(folders)) {
        // A 200 whose body does not match the contract is nobody else's failure to report: the
        // interceptor saw a success, so without this the user just got a silent empty grid.
        showError(t("assets:assets.integrations.snackbars.refresh_failed"));

        return null;
      }

      return folders;
    },
    [showError, t]
  );

  /** Commit a successful import to state. One place, so the two entry points cannot disagree about
   * which of `folders` / `boards` they set. */
  const applyTree = useCallback((tree: IBrowzwearFolder[]) => {
    setFolders(tree);
    setBoards(tree.flatMap((folder) => folder.boards ?? []));
  }, []);

  const connect = useCallback(
    async (creds: BrowzwearCredentials): Promise<boolean> => {
      setIsLoading(true);
      setHasFailed(false);
      const requestId = ++requestIdRef.current;

      try {
        const connectRes = await browzwearService.connect({
          api_key: creds.apiKey,
          api_secret: creds.apiSecret,
          store_name: creds.storeName,
          email: creds.email,
        });

        if (!isCurrent(requestId)) return false;

        if (connectRes?._handled) {
          setHasFailed(true);

          return false;
        }

        const ownerId = connectRes?.responseData?.owner_id;
        if (!ownerId) {
          // A 200 with no owner_id means the EMAIL lookup found nothing — `/connect` resolves the
          // owner with `GET /users/email/<email>` against STYLEZONE, and that address is not
          // necessarily the Refabric login the form pre-fills it with. Measured on the Polytropon
          // store: `ahmet@refabric.com` → 404, `amalia@polytropon.com` → 200. So this branch fires
          // with perfectly valid API credentials and the user is told "failed to connect", which
          // sends them to re-check the key that was never the problem (REF-6498).
          //
          // Still a refusal, not a warning: `owner_id` is what every later export is created under,
          // and the tree-scan fallback would hand Stylezone ANOTHER user's id. But the message now
          // names the field that is actually wrong.
          setHasFailed(true);
          showError(t("common:export_browzwear.error_owner_not_found"));

          return false;
        }

        const imported = await importTree();

        if (!isCurrent(requestId)) return false;

        if (imported === null) {
          setHasFailed(true);

          return false;
        }

        if (!isCurrent(requestId)) return false;

        loadedConnectionRef.current = `${creds.storeName}::${creds.apiKey}`;
        applyTree(imported);
        // Deliberately NOT `...creds`: the api key and secret stay out of Redux now that the server
        // holds them. What is left is display metadata plus the email, which is a Stylezone account
        // address the user may want to see pre-filled on a reconnect — not a credential.
        dispatch(setBrowzwear({ storeName: creds.storeName, email: creds.email, ownerId, isConnected: true }));

        return true;
      } catch (error) {
        if (!isCurrent(requestId)) return false;

        setHasFailed(true);
        // 402 REJECTS (it is not in the `_handled` set) but the paywall snackbar was already
        // dispatched by the interceptor — a second one here is a duplicate, per error-handling.md.
        if ((error as any)?.response?.status !== 402) showError(t("common:export_browzwear.error_connect"));

        return false;
      } finally {
        if (isCurrent(requestId)) setIsLoading(false);
      }
    },
    [applyTree, dispatch, importTree, showError, t]
  );

  /**
   * Re-establish the UI from the SERVER's session after a reload (REF-6498).
   *
   * Redux is memory: a refresh rebuilds it empty, which is why the page used to fall back to the
   * credential form even for an account that was perfectly connected — and why the user had to
   * re-type an API secret to look at a folder list. The credentials now live server-side, so the
   * question "am I connected?" is the server's to answer, and this asks it instead of the user.
   */
  const restore = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    const requestId = ++requestIdRef.current;

    try {
      const res = await browzwearService.status();
      const status = res?.responseData;
      if (!status?.connected) return false;
      if (!isCurrent(requestId)) return false;

      // Claim the connection key we are ABOUT to dispatch, BEFORE dispatching it. The dispatch
      // changes `browzwear.storeName`, which changes `connectionKey`, which fires the reset effect
      // above — and that effect bumps `requestIdRef` and clears the tree. So the import started
      // two lines below would resolve as "superseded" and never reach `applyTree`: the page shows
      // "Updated <time>" (set inside importTree) next to "No boards found", while the exact same
      // flow works when you navigate back to the page instead of reloading, because Redux already
      // holds the store name and the key does not move. `connect()` has always done this; `restore`
      // did not (REF-6498).
      loadedConnectionRef.current = `${status.store_name ?? ""}::${browzwear.apiKey}`;
      dispatch(setBrowzwear({ storeName: status.store_name ?? "", ownerId: status.owner_id ?? "", isConnected: true }));

      const imported = await importTree();
      if (!isCurrent(requestId)) return false;
      if (imported === null) {
        // The session is live but the walk failed — stay connected and let the user hit Refresh,
        // rather than throwing them back to a form whose credentials are not the problem.
        setHasFailed(true);

        return false;
      }

      applyTree(imported);

      return true;
    } catch {
      // No session, or the status call failed. Either way the connect form is the honest answer;
      // it is the DEFAULT view, so nothing to show and nothing to shout about.
      return false;
    } finally {
      if (isCurrent(requestId)) setIsLoading(false);
    }
  }, [applyTree, browzwear.apiKey, dispatch, importTree]);

  useEffect(() => {
    // Only when Redux says "not connected" — i.e. a fresh page load. Inside a session this would be
    // a redundant round trip on every mount of every consumer.
    if (!isConnected) void restore();
    // Mount-only: `restore` changes identity with its deps and this must not re-fire.
  }, []);

  const refresh = useCallback(
    async (force = false): Promise<boolean> => {
      // No credential precondition any more. Before REF-6498 this returned false after every reload —
      // Redux had been rebuilt empty — which is why the page sat on the connect form even for a user
      // whose account was perfectly connected. The server decides now; a caller with no session gets
      // `reconnect_required` and lands in `catch` below.
      if (!isConnected) return false;

      setIsLoading(true);
      setHasFailed(false);
      const requestId = ++requestIdRef.current;

      try {
        const imported = await importTree(force);

        if (!isCurrent(requestId)) return false;

        if (imported === null) {
          setHasFailed(true);

          return false;
        }

        applyTree(imported);

        return true;
      } catch (error) {
        if (!isCurrent(requestId)) return false;

        setHasFailed(true);
        if ((error as any)?.response?.status !== 402) showError(t("assets:assets.integrations.snackbars.refresh_failed"));

        return false;
      } finally {
        if (isCurrent(requestId)) setIsLoading(false);
      }
    },
    [isConnected, applyTree, importTree, showError, t]
  );

  return {
    isConnected,
    credentials: browzwear,
    boards,
    folders,
    syncedAt,
    isLoading,
    hasFailed,
    connect,
    refresh,
    restore,
    disconnect: async () => {
      await browzwearService.disconnect().catch(() => undefined);
      dispatch(setBrowzwear({ storeName: "", email: "", ownerId: "", isConnected: false }));
      setBoards([]);
      setFolders([]);
    },
  };
};
